import styles from './drone-parts-page.module.scss';
import { Button, Input, Select, Space, Table } from "antd";
import layoutStorage from "../../storages/layout-storage";
import { observer } from "mobx-react-lite";
import AddDronePartDialog from "../../dialogs/add-drone-part-dialog";
import PartsList from '../../containers/parts-list/parts-list';
import AddItemVariantDialog from "../../dialogs/add-item-variant-dialog";
import IconAlert from "@ant-design/icons/AlertOutlined";
import SaveIcon from "@ant-design/icons/SaveOutlined";
import { useEffect, useState } from "react";
import { normalizeUrl } from "../../helpers/normalize-url";
import itemsStorage from "../../storages/items-storage";
import brandsStorage from "../../storages/brands";
import partTypesStorage from "../../storages/part-types";
import RestApiQuery from "../../helpers/rest-api-query";
import { IBrand } from "@drone-parts.garphild.pro/interfaces";

/* eslint-disable-next-line */
export interface DronePartsPageProps {}

export function DronePartsPage(props: DronePartsPageProps) {
  const { toggleAddPartModalDialog } = layoutStorage;
  const [checkedUrl, setCheckedUrl] = useState<string>("");
  const [isUrlAlreadyExists, setIsUrlAlreadyExists] = useState<boolean>(false);
  const [checkingStatus, setCheckingStatus] = useState<"idle" | "checking" | "checked">("idle");
  const [brandFilter, setBrandFilter] = useState<string[]>([]);
  const [partTypeFilter, setPartTypeFilter] = useState<string[]>([]);
  const [parsedItems, setParsedItems] = useState<any[]>([]);

  useEffect(() => {
    brandsStorage.loadBrands();
    partTypesStorage.loadPartTypes();
  }, []);

  const checkUrl = (newUrl: string) => {
    const url = normalizeUrl(newUrl);
    const isUrlAlreadyExists = itemsStorage
      .items
      .some((item) => item.variants?.some((variant) => variant.url === url));
    setIsUrlAlreadyExists(isUrlAlreadyExists);
    setCheckedUrl(url);
  }

  const loadParsedItems = (url: string) => {
    setCheckingStatus("checking");
    RestApiQuery
      .post<{ url: string }, any>('/api/parsers', { url })
      .then((response) => {
        setCheckingStatus("checked");
        if (response) {
          const possibleBrands = response
            .reduce((acc: string[], item: any) => [...acc, ...item.possibleBrands], [])
            .map((brand: string) => {
              const brandName = brand.toLowerCase();
              const brandFromStorage = brandsStorage.brands.find((brand) => brand.name.toLowerCase() === brandName);
              return brandFromStorage?._id || null;
            })
            .filter((brandId: IBrand['_id'] | null) => brandId !== null);
          const possiblePartTypes = response
            .reduce((acc: string[], item: any) => [...acc, ...item.possiblePartTypes], [])
            .map((partType: string) => {
              const partTypeName = partType.toLowerCase();
              const partTypeFromStorage = partTypesStorage.partTypes.find((partType) => partType.name.toLowerCase() === partTypeName);
              return partTypeFromStorage?.key || null;
            })
            .filter((partTypeId: number | null) => partTypeId !== null);
          setBrandFilter(possibleBrands);
          setPartTypeFilter(possiblePartTypes);
          setParsedItems(response.map((item: any) => ({
            ...item,
            selectedValues: {
              partTypeId: partTypesStorage.partTypes.find((partType) => partType.name === item.possiblePartTypes[0])?.key,
              brandId: brandsStorage.brands.find((brand) => brand.name === item.possibleBrands[0])?._id,
            },
          })));
        }
      })
      .catch(() => {
        setCheckingStatus("checked");
      });
  }
  return (
    <div className={styles['container']}>
      <Button type="primary" onClick={toggleAddPartModalDialog}>Add</Button>
      <Space direction="vertical">
        <Input
          // ref={searchInput}
          disabled={checkingStatus === "checking"}
          placeholder={`Check url`}
          value={checkedUrl}
          onChange={(e) => checkUrl(e.target.value)}
          style={{ marginBottom: 8, display: 'block' }}
          addonAfter={(
            <>
              {isUrlAlreadyExists && <span className={styles['error']} onClick={() => loadParsedItems(checkedUrl)}><IconAlert color={isUrlAlreadyExists ? 'green' : 'lightgray'} /></span>}
              &nbsp;&nbsp;
              <span className={styles['error']} onClick={() => loadParsedItems(checkedUrl)}><SaveIcon /></span>
            </>
          )}
        />
        <Table
          columns={[
            {
              title: "Name",
              dataIndex: "name",
            },
            {
              title: "Price",
              dataIndex: "price",
            },
            {
              title: "Brand",
              dataIndex: "possibleBrands",
              render: (possibleBrands: string[], row, dataIndex) => (
                <Space direction="vertical">
                  <span>{possibleBrands?.join(", ")}</span>
                  <Select
                    showSearch
                    filterOption={(input: string, option?: { label: string; value: string }) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                    style={{ width: 150 }}
                    value={row.selectedValues?.brandId || null}
                    options={brandsStorage.brands.map((brand) => ({ label: brand.name, value: brand._id?.toString() || 'n/a' }))}
                    onChange={(value) => {
                      setParsedItems(parsedItems.map((item, index) => {
                        if (index === dataIndex) {
                          return {
                            ...item,
                            selectedValues: {
                              ...item.selectedValues,
                              brandId: value,
                            },
                          };
                        }
                        return item;
                      }));
                    }}
                  />
                </Space>
              ),
            },
            {
              title: "Part type",
              dataIndex: "possiblePartTypes",
              render: (possiblePartTypes: string[], row, dataIndex) => (
                <Space direction="vertical">
                  <span>{possiblePartTypes.join(", ")}</span>
                  <Select
                    showSearch
                    style={{ width: 150 }}
                    value={row.selectedValues?.partTypeId || null}
                    options={partTypesStorage.partTypes.map((partType) => ({ label: partType.name, value: partType.key }))}
                    onChange={(value) => {
                      setParsedItems(parsedItems.map((item, index) => {
                        if (index === dataIndex) {
                          return {
                            ...item,
                            selectedValues: {
                              ...item.selectedValues,
                              partTypeId: value,
                            },
                          };
                        }
                        return item;
                      }));
                    }}
                  />
                </Space>
              ),
            },
            {
              title: "possibleVariants",
              dataIndex: "possibleVariants",
              render: (value: any, row, dataIndex) => (
                <Space direction="vertical">
                  <Select
                    style={{ width: 150 }}
                    value={row.selectedValues?.possibleVariant || null}
                    options={itemsStorage
                      .items
                      .filter((item) => {
                        if (row.selectedValues?.brandId) {
                          if (item.brandId !== row.selectedValues?.brandId) {
                            return false;
                          }
                        }
                        if (row.selectedValues?.partTypeId) {
                          if (item.partTypeId !== row.selectedValues?.partTypeId) {
                            return false;
                          }
                        }
                        return true;
                      })
                      .map((item) => ({ label: item.name, value: item._id }))
                  }
                  />
                </Space>
              ),
            }
          ]}
          dataSource={parsedItems}
        />
      </Space>
      <PartsList
        filters={{
          brandId: brandFilter,
          partTypeId: partTypeFilter,
        }}
      />
      <AddDronePartDialog />
      <AddItemVariantDialog />
    </div>
  );
}

export default observer(DronePartsPage);
